import {
  createFileRoute,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router'
import { useEffectOnce } from 'react-use'
import { z } from 'zod'

import { queryClient } from '@/api/api'
import { assignTaskToUserOptions } from '@/api/codegen/@tanstack/react-query.gen'
import { useViewTaskQuery } from '@/api/domain/task/task'
import { Loading } from '@/components/ui/Loading'
import { useSidebar } from '@/components/ui/Sidebar'
import { SidebarKeyMap } from '@/constants/sidebar.constants'
import { TaskBasicPage } from '@/pages/task-basic/TaskBasic.page'
import { TaskVideoAnnotationPage } from '@/pages/task-objectTracking/TaskVideoAnnotation.page'

const taskViewSearchSchema = z.object({
  projectId: z.string(),
  returnTo: z.string(),
})

const TaskViewPageContainer = () => {
  const { setOpen } = useSidebar(SidebarKeyMap.MAIN)
  const navigate = useNavigate()
  const { taskId } = useParams({ from: '/_auth/task-view/$taskId' })
  const { projectId } = useSearch({ from: '/_auth/task-view/$taskId' })

  const {
    data: task,
    isFetching,
    isPending,
    isError,
  } = useViewTaskQuery({ projectId, taskId })

  useEffectOnce(() => {
    setOpen(false)

    return () => {
      setOpen(true)
      queryClient.removeQueries({
        queryKey: assignTaskToUserOptions().queryKey,
      })
    }
  })

  if (isFetching || isPending) {
    return <Loading />
  }

  if (isError) {
    navigate({ to: '/dashboard' })

    return null
  }

  if (task.type === 'video_annotation') {
    queryClient.setQueryData(assignTaskToUserOptions().queryKey, task)

    return (
      <TaskVideoAnnotationPage
        task={task}
        isNewTaskModalEnable={false}
        isReadOnly
      />
    )
  }

  return <TaskBasicPage task={task} isNewTaskModalEnable={false} isReadOnly />
}

export const Route = createFileRoute('/_auth/task-view/$taskId')({
  component: TaskViewPageContainer,
  validateSearch: taskViewSearchSchema,
  loader: ({ params }) => ({ breadcrumbTitle: `View Task (${params.taskId})` }),
})
